/**
 * Campaign tracked-link primitives (PURE — no I/O, safe on edge + client).
 *
 * A WhatsApp campaign template's URL button is `https://<tenant host>/c/{{1}}`
 * where `{{1}}` is the opaque token stored on `campaign_sends.token`. The
 * `(booking)/[locale]/booking/c/[token]` route on every booking host resolves
 * it, logs a HUMAN click, sets the `tk_c` cookie and 302s to the landing page.
 * `createReservation` later copies the cookie value into
 * `reservations.metadata.campaign_token`, which the Postgres trigger
 * `fn_campaign_attribute_reservation` (migration 1789858000000) resolves.
 *
 * Everything host- or DB-dependent lives in `campaign-link.server.ts`.
 */

/** Cookie carrying the last clicked tracked-link token on a booking host. */
export const CAMPAIGN_COOKIE = 'tk_c'

/**
 * Cookie carrying the coupon code a tracked link auto-applies (`offer.coupon_code`
 * of the clicked campaign, only when that coupon is active and inside its
 * booking window at click time). Same attributes and lifetime as `tk_c`. It is
 * the campaign twin of the agency `booking_ref` cookie: read server-side by the
 * booking landing (SearchForm prefill), the property page (sidebar prefill) and
 * `/search` (→ `booking_sessions.coupon_code`), which is the ONE guest-coupon
 * path the checkout already validates and applies.
 */
export const CAMPAIGN_COUPON_COOKIE = 'tk_cc'

/** Cookie lifetime: 14 days, in seconds. */
export const CAMPAIGN_COOKIE_MAX_AGE_SECONDS = 14 * 24 * 60 * 60

/**
 * The reservations.metadata key the attribution trigger reads
 * (`NEW.metadata ->> 'campaign_token'`). ONE definition; a test pins it against
 * the migration text.
 */
export const CAMPAIGN_TOKEN_METADATA_KEY = 'campaign_token'

/** Opaque base64url token, 12-32 chars (issued as randomBytes(9) = 12 chars). */
const TOKEN_RE = /^[A-Za-z0-9_-]{12,32}$/

/** True when `s` has the exact shape of a tracked-link token. */
export function isValidCampaignToken(s: unknown): s is string {
  return typeof s === 'string' && TOKEN_RE.test(s)
}

/**
 * Link previewers, crawlers and scripted fetchers. WhatsApp itself fetches every
 * URL button target for the preview card, so this list is what keeps
 * `click_count` meaning "a person tapped it".
 */
const BOT_UA_RE =
  /facebookexternalhit|WhatsApp\/|Facebot|Twitterbot|Slackbot|LinkedInBot|TelegramBot|Googlebot|bingbot|HeadlessChrome|python-requests|curl\//i

/** Header lookup used by `classifyUserAgent`; a subset of the Fetch `Headers` API. */
export interface HeaderReader {
  get(name: string): string | null
}

/** Options for `classifyUserAgent`. */
export interface ClassifyOptions {
  /** HTTP method of the request; HEAD is always a bot/previewer. */
  method?: string
}

/**
 * Classify a request as a human tap or an automated fetch.
 *
 * Rules (any one => 'bot'):
 * - user-agent matches a known previewer / crawler / scripted client
 * - HEAD request
 * - no `Accept: text/html` AND no `Sec-Fetch-Mode` header (browsers send at
 *   least one of the two on a top-level navigation; scripted fetchers rarely do)
 */
export function classifyUserAgent(
  ua: string | null | undefined,
  headers?: HeaderReader | null,
  opts: ClassifyOptions = {}
): 'human' | 'bot' {
  if ((opts.method ?? 'GET').toUpperCase() === 'HEAD') return 'bot'
  if (!ua || BOT_UA_RE.test(ua)) return 'bot'
  if (headers) {
    const accept = headers.get('accept') ?? ''
    const secFetchMode = headers.get('sec-fetch-mode')
    if (!accept.includes('text/html') && !secFetchMode) return 'bot'
  }
  return 'human'
}

/**
 * Build the public tracked link for a token on a tenant's public booking host.
 * `tenantPublicHost` is a bare host (`www.mirada.sa`, `mirada.book.taskyinn.sa`);
 * a scheme or path is stripped so callers can pass either form.
 */
export function buildCampaignLinkUrl(tenantPublicHost: string, token: string): string {
  const host = tenantPublicHost
    .trim()
    .replace(/^https?:\/\//i, '')
    .replace(/\/.*$/, '')
    .toLowerCase()
  if (!host) throw new Error('buildCampaignLinkUrl: host is required')
  if (!isValidCampaignToken(token)) throw new Error('buildCampaignLinkUrl: invalid token')
  return `https://${host}/c/${token}`
}

/**
 * Cookie attributes for `tk_c`. HttpOnly (the client never reads it — the
 * checkout server action re-reads it), Secure, SameSite=Lax (survives the
 * WhatsApp -> browser hop and same-site navigations), Path=/, HOST-ONLY (no
 * `domain` attribute, so it never leaks across `*.book.taskyinn.sa` tenants).
 */
export function campaignCookieOptions(opts: { secure?: boolean } = {}): {
  httpOnly: true
  secure: boolean
  sameSite: 'lax'
  path: '/'
  maxAge: number
} {
  return {
    httpOnly: true,
    secure: opts.secure ?? true,
    sameSite: 'lax',
    path: '/',
    maxAge: CAMPAIGN_COOKIE_MAX_AGE_SECONDS,
  }
}

/**
 * A `campaigns.landing_path` is only honoured when it is a RELATIVE path on the
 * same host: starts with a single `/`, no scheme, no `//` (protocol-relative),
 * no backslash, no CR/LF. Absolute URLs are rejected — the tracked link must
 * never become an open redirect.
 */
export function isSafeLandingPath(p: unknown): p is string {
  if (typeof p !== 'string') return false
  if (p.length === 0 || p.length > 512) return false
  if (!p.startsWith('/') || p.startsWith('//')) return false
  if (/[\\\r\n]/.test(p)) return false
  if (/^\/[A-Za-z][A-Za-z0-9+.-]*:/.test(p)) return false
  return true
}

/**
 * Prefix a same-host path with the request locale unless it already carries one.
 * `/property/MGJ` + `ar` -> `/ar/property/MGJ`; `/en/x` stays `/en/x`.
 */
export function localizeLandingPath(path: string, locale: string, locales: readonly string[]): string {
  const first = path.split('/')[1] ?? ''
  if (locales.includes(first)) return path
  return `/${locale}${path === '/' ? '' : path}`
}

/** Inputs for `pickCampaignLanding`. */
export interface LandingInput {
  /** `campaigns.landing_path` (may be null / unsafe — validated here). */
  landingPath: string | null | undefined
  /** `properties.code` of the sending property (route param of `/property/[code]`). */
  propertyCode: string | null | undefined
  /** True on `{property}.{tenant}.book...` or a property-level custom domain: root IS the property page. */
  hostIsPropertyLevel: boolean
  locale: string
  locales: readonly string[]
}

/**
 * Where a validated click lands, as a same-host path with a locale prefix:
 *   1. a SAFE relative `landing_path` (localised)
 *   2. else the sending property's page `/{locale}/property/{code}` on a
 *      tenant-level host, or the root on a property-level host
 *   3. else the root
 * Never an absolute URL — the tracked link is not an open redirect.
 */
export function pickCampaignLanding(input: LandingInput): string {
  const { locale, locales } = input
  if (isSafeLandingPath(input.landingPath)) {
    return localizeLandingPath(input.landingPath, locale, locales)
  }
  if (!input.hostIsPropertyLevel && input.propertyCode) {
    return `/${locale}/property/${encodeURIComponent(input.propertyCode)}`
  }
  return `/${locale}`
}

/** Coupon codes are short upper-case tokens (`coupons.code`, ≤ 50 chars, no spaces). */
const COUPON_CODE_RE = /^[A-Za-z0-9_-]{2,50}$/

/** True when `s` has the shape of a coupon code (never trusted beyond that). */
export function isValidCouponCode(s: unknown): s is string {
  return typeof s === 'string' && COUPON_CODE_RE.test(s)
}

/** The coupon columns the click-time check needs. */
export interface CouponValidityRow {
  code: string
  is_active: boolean
  /** timestamptz / date string. */
  valid_from: string | null
  valid_until: string | null
}

/**
 * Coupon code the tracked link should auto-apply, or null.
 *   - the campaign offer must name a coupon (`offer.coupon_code`)
 *   - the looked-up `coupons` row must exist, be `is_active`, and be inside its
 *     booking window: `valid_from <= now < end of valid_until's day` — the same
 *     rule `isWithinBookingWindow` in `services/coupon.ts` applies at checkout,
 *     so a click never carries a code the checkout would then refuse.
 * Expired / inactive / unknown → null (the click still lands, without coupon).
 */
export function pickCampaignCoupon(input: {
  offerCouponCode: string | null | undefined
  coupon: CouponValidityRow | null | undefined
  now?: Date
}): string | null {
  const wanted = (input.offerCouponCode ?? '').trim().toUpperCase()
  if (!isValidCouponCode(wanted)) return null
  const c = input.coupon
  if (!c || !c.is_active) return null
  if (c.code.toUpperCase() !== wanted) return null
  const now = (input.now ?? new Date()).getTime()
  if (c.valid_from && new Date(c.valid_from).getTime() > now) return null
  if (c.valid_until) {
    const eod = new Date(c.valid_until).getTime() + 24 * 60 * 60 * 1000
    if (Number.isNaN(eod) || eod < now) return null
  }
  return c.code
}

/** `offer.coupon_code` of a `campaigns.offer` jsonb value, upper-cased, or null. */
export function offerCouponCode(offer: unknown): string | null {
  if (!offer || typeof offer !== 'object') return null
  const v = (offer as Record<string, unknown>).coupon_code
  return isValidCouponCode(v) ? v.toUpperCase() : null
}
